'use client';

import { Popover as BasePopover } from '@base-ui/react/popover';
import { useId, useRef } from 'react';

import {
  type DeprecatedPlacementValue,
  type PlacementValue,
} from '../../internal/types/placement';
import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Surface, type SurfaceProps } from '../surface/Surface';
import { usePopoverContext } from './internal/PopoverContext';
import {
  PopoverPopupArrow,
  popoverPopupArrowClasses,
} from './internal/PopoverPopupArrow';

const popoverPopupClasses = {
  panel: 'zds-popover-popup-panel',
  arrow: popoverPopupArrowClasses.root,
} as const;

const popoverPopupStyles = tv({
  slots: {
    panel: [popoverPopupClasses.panel, 'w-full outline-none'],
  },
  variants: {
    disablePadding: { false: { panel: 'p-4' } },
    width: {
      auto: '',
      trigger: { panel: 'w-[var(--anchor-width)]' },
      'min-trigger': { panel: 'min-w-[var(--anchor-width)]' },
    },
  },
});

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

const DEPRECATED_PLACEMENT_MAP: Record<
  DeprecatedPlacementValue,
  PlacementValue
> = {
  top: 'top-center',
  bottom: 'bottom-center',
  left: 'left-center',
  right: 'right-center',
  'top-start': 'top-left',
  'top-end': 'top-right',
  'bottom-start': 'bottom-left',
  'bottom-end': 'bottom-right',
  'left-start': 'left-top',
  'left-end': 'left-bottom',
  'right-start': 'right-top',
  'right-end': 'right-bottom',
};

function resolvePlacement(
  p: PlacementValue | DeprecatedPlacementValue
): PlacementValue {
  return (
    (DEPRECATED_PLACEMENT_MAP as Record<string, PlacementValue>)[p] ??
    (p as PlacementValue)
  );
}

/**
 * Splits a `Placement` string (e.g. `'bottom-left'`, `'right-top'`) into Base UI's
 * `side` and `align` props.
 *
 * Alignment suffix is always the literal cross-axis direction:
 * - top/bottom sides: `left` → `start`, `center` → `center`, `right` → `end`
 * - left/right sides: `top` → `start`, `center` → `center`, `bottom` → `end`
 */
function splitPlacement(placement: PlacementValue): {
  side: Side;
  align: Align;
} {
  const [side, alignRaw] = placement.split('-') as [Side, string];
  const align: Align =
    alignRaw === 'left' || alignRaw === 'top'
      ? 'start'
      : alignRaw === 'right' || alignRaw === 'bottom'
        ? 'end'
        : 'center';
  return { side, align };
}

interface PopoverPopupBaseProps {
  /**
   * The placement of the Popover relative to its trigger.
   *
   * Combines a side (`'top'`, `'bottom'`, `'left'`, `'right'`) with an
   * alignment suffix: `'left'`/`'center'`/`'right'` for top/bottom sides,
   * `'top'`/`'center'`/`'bottom'` for left/right sides.
   *
   * The bare side values (`'top'`, `'bottom'`, etc.) and `-start`/`-end`
   * suffixes are deprecated. Use `'top-center'`, `'bottom-left'`,
   * `'right-bottom'`, etc. instead.
   *
   * @default 'bottom-center'
   */
  placement?: PlacementValue | DeprecatedPlacementValue;
  /**
   * The offset distance in pixels between the Popover and its trigger.
   *
   * @default 12
   */
  offset?: number;
  /**
   * Disable flipping the popup to the opposite side when there is not enough space.
   *
   * @default false
   */
  disableFlip?: boolean;
  /**
   * Disable shifting the popup along its alignment axis when there is not enough space.
   *
   * @default false
   */
  disableShift?: boolean;
  /**
   * Controls the rendered width of the popup relative to its trigger.
   *
   * - `'auto'`: content drives width.
   * - `'trigger'`: width matches trigger.
   * - `'min-trigger'`: min-width matches trigger; content can grow wider.
   *
   * @default 'auto'
   */
  width?: 'auto' | 'trigger' | 'min-trigger';
  /**
   * How the popup manages its DOM presence.
   *
   * - `'lazy'`: starts unmounted; mounts on open, unmounts on close.
   * - `'eager'`: mounted at all times. Use to retain local state across open/close cycles.
   *
   * @default 'lazy'
   */
  mountStrategy?: 'lazy' | 'eager';
  /**
   * If `true`, the Popover will render in the normal DOM flow instead of a React portal.
   *
   * @default false
   */
  disablePortal?: boolean;
  /**
   * Whether to disable the default padding applied to the Popover popup.
   *
   * @default false
   */
  disablePadding?: boolean;
  /**
   * Whether to show the arrow pointing to the trigger element.
   *
   * @default false
   */
  showArrow?: boolean;
  /**
   * The style of the Popover panel.
   *
   * @default 'elevated'
   */
  variant?: SurfaceProps['variant'];
  /**
   * The elevation level (drop-shadow size) of the Popover popup. Only applies if `variant="elevated"`.
   *
   * @default 1
   */
  elevation?: SurfaceProps['elevation'];
  /**
   * Popup content. Use this for new code.
   *
   * For accessible labelling, set `aria-labelledby` on the Popup pointing to a heading id (or use the trigger's id), or `aria-label` for a screen-reader-only label.
   */
  children?: React.ReactNode;
  /**
   * @deprecated Use `children` instead and wire `aria-labelledby` / `aria-label` on `Popover.Popup` directly.
   *
   * @example
   * // Before
   * <Popover.Popup render={({ title }) => <h2 {...title}>Heading</h2>} />
   *
   * // After
   * <Popover.Popup aria-labelledby="popup-heading">
   *   <Typography component="h2" id="popup-heading">Heading</Typography>
   * </Popover.Popup>
   */
  render?: (
    props: { title: { id: string | undefined } },
    state: { readonly isOpen: boolean }
  ) => React.ReactNode;
}

type PopoverPopupProps = ComponentProps<PopoverPopupBaseProps, 'div'>;

type PopoverPopupComponent = Component<PopoverPopupBaseProps, 'div'>;

const PopoverPopup: PopoverPopupComponent = (props) => {
  const ctx = usePopoverContext();
  const titleId = useId();
  const inlineContainerRef = useRef<HTMLSpanElement>(null);

  const {
    placement = ctx.placement ?? 'bottom-center',
    offset = ctx.offset ?? 12,
    disableFlip = ctx.disableFlip ?? false,
    disableShift = ctx.disableShift ?? false,
    width = ctx.width ?? 'auto',
    mountStrategy = ctx.mountStrategy ?? 'lazy',
    disablePortal = ctx.disablePortal ?? false,
    disablePadding = false,
    showArrow = false,
    elevation = 1,
    variant = 'elevated',
    children,
    render,
    className,
    ref,
    ...rest
  } = props;

  const { side, align } = splitPlacement(resolvePlacement(placement));
  const styles = popoverPopupStyles({
    disablePadding,
    width,
  });

  return (
    <>
      {/* Inline mount point for `disablePortal`. Base UI's `Positioner` requires
          Portal context, so we always render `Portal` — but when `disablePortal`
          is set we redirect its `container` to this local element so the popup
          lands in the normal DOM flow instead of `document.body`.
          `display: contents` makes the span transparent to layout so it does
          not affect sibling selectors or flex/grid gap calculations on the
          consumer's parent element. */}
      {disablePortal ? (
        <span ref={inlineContainerRef} className="contents" />
      ) : null}
      <BasePopover.Portal
        keepMounted={mountStrategy === 'eager'}
        container={disablePortal ? inlineContainerRef : undefined}
        // `BasePopover.Portal` renders a real `<div>` wrapper inside its container.
        // When portalling to `document.body` that is harmless, but with
        // `disablePortal` the wrapper becomes an in-flow child of the consumer's
        // layout container (often `flex`/`grid`), taking a layout slot and
        // pushing siblings off-axis. `display: contents` makes the wrapper
        // transparent to layout so only the absolutely-positioned Positioner
        // inside it contributes to painting.
        className={disablePortal ? 'contents' : undefined}
      >
        <BasePopover.Positioner
          side={side}
          align={align}
          sideOffset={offset}
          collisionAvoidance={{
            side: disableFlip ? 'none' : 'flip',
            align: disableShift ? 'none' : 'shift',
          }}
          className="z-9999"
        >
          <BasePopover.Popup
            {...rest}
            ref={ref}
            className={styles.panel({ className })}
            // When the deprecated `render` callback is in use, label the popup via the id
            // we pass into the callback's `title.id`. Consumer spreads `{...props.title}`
            // on their heading element; the popup picks it up here.
            aria-labelledby={render ? titleId : rest['aria-labelledby']}
            render={(popupProps, popupState) => {
              // `popupProps.color` is the legacy HTML `color` attribute (typed `string`).
              // `Surface` narrows `color` to `'primary' | 'neutral'`. Drop the HTML attr
              // before spreading to avoid a type clash; the visual colour is driven by
              // `variant` and `elevation` instead.
              const { color: _color, ...safePopupProps } = popupProps;
              return (
                <Surface
                  {...safePopupProps}
                  variant={variant}
                  elevation={elevation}
                >
                  {showArrow ? <PopoverPopupArrow variant={variant} /> : null}
                  {render
                    ? render(
                        { title: { id: titleId } },
                        { isOpen: popupState.open }
                      )
                    : children}
                </Surface>
              );
            }}
          />
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </>
  );
};

PopoverPopup.displayName = 'Popover.Popup';

export {
  PopoverPopup,
  resolvePlacement,
  splitPlacement,
  type PopoverPopupProps,
  type PopoverPopupBaseProps,
  type PlacementValue,
  type DeprecatedPlacementValue,
  popoverPopupClasses,
};
